import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  PackageX,
  Search,
  Users,
  X,
  PenLine,
  AlertCircle,
} from 'lucide-react';
import {
  STATIONS,
  type Station,
  type IssuedKitItem,
  type KitIssuedPayload,
  type KitReturnedPayload,
  type MobilisationEvent,
} from '@hcc/types';
import { useCurrentUser } from '../../hooks/useAuth';
import {
  usePeopleList,
  usePersonEvents,
  useAppendEvent,
  type PersonApi,
} from '../../hooks/usePeople';
import { useIntentKey } from '../../hooks/useIntentKey';
import { SignaturePadModal } from '../../components/kit/SignaturePadModal';
import { Button } from '../../components/ui/Button';
import { SkeletonList } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { ApiError } from '../../lib/api';

/**
 * Leaver-return flow (Phase 3 item 22). The reverse of KitIssued:
 * captures a signature for kit handed back when someone leaves the
 * contract. "Currently held" is computed client-side from the event
 * history as KitIssued items minus already-returned items, matched by
 * (station, name). Quantities subtract; if a leaver hands back fewer
 * than they were issued, the remainder stays on the held list.
 *
 * Same append flow as KitIssued, just with a different event_type.
 */
export function LeaverReturn() {
  const { data: user, isLoading: meLoading } = useCurrentUser();

  const [q, setQ] = useState('');
  // No `arrived` filter: a leaver might be off-site by the time their
  // kit comes back, especially weeks later.
  const list = usePeopleList(q);
  const [activePersonId, setActivePersonId] = useState<string | null>(null);

  if (meLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  const allowed =
    user.roles.includes('admin') ||
    user.roles.includes('coordinator') ||
    user.roles.includes('hr');
  if (!allowed) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        You do not have permission to record kit returns.
      </main>
    );
  }

  const activePerson =
    activePersonId
      ? (list.data ?? []).find((p) => p.id === activePersonId) ?? null
      : null;

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
            <PackageX className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-accent">
              Admin
            </div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Leaver kit return
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              Record kit handed back when someone leaves the contract.
              Currently-held kit is computed from issuance history.
            </p>
          </div>
        </header>

        <div className="relative mt-6">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-fg-dim" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email..."
            className="block h-12 w-full rounded-lg border border-line-strong/20 bg-surface pl-11 pr-3 text-base shadow-sm transition-colors focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
          />
        </div>

        <div className="mt-4 overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
          {list.isLoading ? (
            <SkeletonList rows={6} />
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState
              icon={<Users className="h-6 w-6" />}
              title="No matches"
              description={q ? `Nothing for "${q}".` : 'No people in the system yet.'}
            />
          ) : (
            <ul className="divide-y divide-divider/10">
              {list.data.map((p) => (
                <li
                  key={p.id}
                  className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-base font-semibold">{p.full_name}</div>
                    <div className="mt-0.5 text-xs text-fg-muted">
                      {p.position ?? 'Position unknown'}
                      {p.email ? ` · ${p.email}` : ''}
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    size="md"
                    iconLeft={<PackageX className="h-4 w-4" />}
                    onClick={() => setActivePersonId(p.id)}
                  >
                    Record return
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {activePerson ? (
        <ReturnDrawer
          person={activePerson}
          onClose={() => setActivePersonId(null)}
        />
      ) : null}
    </main>
  );
}

interface HeldRow {
  station: Station;
  name: string;
  quantity: number;
  /** Items aggregated together; preserves serial/registration/size when a single source row is unique. */
  serial?: string;
  registration?: string;
  size?: string;
}

/**
 * Compute still-held kit for one person. Aggregate KitIssued items by
 * (station, name), subtract aggregated KitReturned items by the same
 * key. We prefer to surface serial/registration/size when only one
 * source row contributed; otherwise leave them blank for the operator
 * to fill in.
 */
function computeHeld(events: readonly MobilisationEvent[]): HeldRow[] {
  type Bucket = {
    station: Station;
    name: string;
    qty: number;
    sourceRows: IssuedKitItem[];
  };
  const issued = new Map<string, Bucket>();

  for (const ev of events) {
    if (ev.event_type === 'KitIssued') {
      const p = ev.payload as KitIssuedPayload;
      for (const it of p.items) {
        const key = `${p.station}\0${it.name.toLowerCase()}`;
        const b = issued.get(key) ?? {
          station: p.station,
          name: it.name,
          qty: 0,
          sourceRows: [],
        };
        b.qty += it.quantity;
        b.sourceRows.push(it);
        issued.set(key, b);
      }
    } else if (ev.event_type === 'KitReturned') {
      const p = ev.payload as KitReturnedPayload;
      for (const it of p.items) {
        const key = `${p.station}\0${it.name.toLowerCase()}`;
        const b = issued.get(key);
        if (b) b.qty = Math.max(0, b.qty - it.quantity);
      }
    }
  }

  const rows: HeldRow[] = [];
  for (const b of issued.values()) {
    if (b.qty <= 0) continue;
    const onlySource = b.sourceRows.length === 1 ? b.sourceRows[0] : undefined;
    rows.push({
      station: b.station,
      name: b.name,
      quantity: b.qty,
      serial: onlySource?.serial,
      registration: onlySource?.registration,
      size: onlySource?.size,
    });
  }
  rows.sort((a, b) => a.station.localeCompare(b.station) || a.name.localeCompare(b.name));
  return rows;
}

function ReturnDrawer({ person, onClose }: { person: PersonApi; onClose: () => void }) {
  const events = usePersonEvents(person.id);
  const append = useAppendEvent(person.id);
  const returnIntent = useIntentKey();

  const held = useMemo(() => computeHeld(events.data ?? []), [events.data]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [returnQty, setReturnQty] = useState<Record<string, number>>({});
  const [station, setStation] = useState<Station>('IT');
  const [notes, setNotes] = useState('');
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function rowKey(r: HeldRow) {
    return `${r.station}\0${r.name}`;
  }

  function toggle(r: HeldRow) {
    const k = rowKey(r);
    setSelected((s) => ({ ...s, [k]: !s[k] }));
    setReturnQty((s) => ({ ...s, [k]: s[k] ?? r.quantity }));
    setStation(r.station);
  }

  function chosenItems(): IssuedKitItem[] {
    return held
      .filter((r) => selected[rowKey(r)] && r.station === station)
      .map((r) => {
        const qty = Math.max(1, Math.min(r.quantity, returnQty[rowKey(r)] ?? r.quantity));
        return {
          name: r.name,
          quantity: qty,
          ...(r.size ? { size: r.size } : {}),
          ...(r.serial ? { serial: r.serial } : {}),
          ...(r.registration ? { registration: r.registration } : {}),
        };
      });
  }

  async function onSignatureConfirm(dataUrl: string) {
    setError(null);
    const items = chosenItems();
    if (items.length === 0) {
      setError('Pick at least one item to return');
      setSigning(false);
      return;
    }
    try {
      await append.mutateAsync({
        event_type: 'KitReturned',
        payload: {
          station,
          items,
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
        signature_png: dataUrl,
        client_event_id: returnIntent.key,
      } as Parameters<typeof append.mutateAsync>[0]);
      returnIntent.renew();
      setSigning(false);
      toast.success(`${station} return recorded for ${person.full_name}`);
      onClose();
    } catch (err) {
      setSigning(false);
      const msg = err instanceof ApiError ? err.message : 'failed to record return';
      setError(msg);
      toast.error(msg);
    }
  }

  const stationsWithHeld = Array.from(new Set(held.map((r) => r.station))) as Station[];

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
        aria-hidden
      />
      <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-xl flex-col border-l border-line/10 bg-surface shadow-xl animate-slide-in-right">
        <header className="flex items-start justify-between gap-3 border-b border-line/10 p-5 sm:p-6">
          <div className="min-w-0">
            <div className="text-xs font-medium uppercase tracking-wider text-accent">
              Leaver return
            </div>
            <h2 className="mt-0.5 truncate text-xl font-semibold tracking-tight">
              {person.full_name}
            </h2>
            <p className="text-xs text-fg-muted">
              {person.position ?? 'Position unknown'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1.5 text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
          {events.isLoading ? (
            <SkeletonList rows={4} />
          ) : held.length === 0 ? (
            <div className="flex items-start gap-2 rounded-md bg-warning-soft/15 px-3 py-2 text-sm text-warning-soft-fg">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>No kit currently held.</span>
            </div>
          ) : (
            <>
              <label className="block text-sm">
                <span className="font-medium text-fg-muted">
                  Returning to station
                </span>
                <select
                  value={station}
                  onChange={(e) => setStation(e.target.value as Station)}
                  className="mt-1.5 block h-10 rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
                >
                  {STATIONS.map((s) => (
                    <option
                      key={s}
                      value={s}
                      disabled={!stationsWithHeld.includes(s)}
                    >
                      {s}
                      {stationsWithHeld.includes(s) ? '' : ' (nothing held)'}
                    </option>
                  ))}
                </select>
                <span className="mt-1 block text-xs text-fg-muted">
                  One signature per station; if a leaver hands back kit
                  from multiple stations, record each separately.
                </span>
              </label>

              <h3 className="mt-6 text-sm font-semibold text-fg-muted">
                Currently held
              </h3>
              <div className="mt-2 overflow-hidden rounded-md border border-line/10">
                <table className="w-full text-left text-sm">
                  <thead className="bg-surface-2 text-xs uppercase tracking-wider text-fg-muted">
                    <tr>
                      <th className="px-3 py-2"></th>
                      <th className="px-3 py-2">Station</th>
                      <th className="px-3 py-2">Item</th>
                      <th className="px-3 py-2">Held</th>
                      <th className="px-3 py-2">Returning</th>
                    </tr>
                  </thead>
                  <tbody>
                    {held.map((r) => {
                      const k = rowKey(r);
                      const checked = !!selected[k];
                      const inStation = r.station === station;
                      return (
                        <tr
                          key={k}
                          className={`border-t border-line/10 transition-colors ${
                            inStation ? 'hover:bg-surface-2/50' : 'opacity-50'
                          }`}
                        >
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={!inStation}
                              onChange={() => toggle(r)}
                              className="h-4 w-4 rounded border-line-strong/20"
                            />
                          </td>
                          <td className="px-3 py-2 text-xs">
                            <span className="rounded bg-surface-2 px-1.5 py-0.5 font-semibold uppercase tracking-wider text-fg-muted">
                              {r.station}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="font-medium">{r.name}</div>
                            {r.serial ? (
                              <div className="text-xs text-fg-muted">#{r.serial}</div>
                            ) : null}
                            {r.registration ? (
                              <div className="text-xs text-fg-muted">
                                {r.registration}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 tabular-nums">{r.quantity}</td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              min={1}
                              max={r.quantity}
                              disabled={!checked || !inStation}
                              value={returnQty[k] ?? r.quantity}
                              onChange={(e) =>
                                setReturnQty((s) => ({
                                  ...s,
                                  [k]: Math.max(
                                    1,
                                    Math.min(
                                      r.quantity,
                                      Number(e.target.value) || 1,
                                    ),
                                  ),
                                }))
                              }
                              className="w-16 rounded-md border border-line-strong/20 bg-surface px-2 py-1 text-sm shadow-sm disabled:bg-surface-2"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <h3 className="mt-6 text-sm font-semibold text-fg-muted">
                Notes <span className="font-normal text-fg-dim">(optional)</span>
              </h3>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="e.g. damaged returned items, missing parts, replacement plan"
                className="mt-1 block w-full rounded-md border border-line-strong/20 bg-surface px-3 py-2 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
              />

              {error ? (
                <div className="mt-4 flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              ) : null}
            </>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-line/10 bg-surface-2 p-4 sm:p-5">
          <Button variant="secondary" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            iconLeft={<PenLine className="h-4 w-4" />}
            disabled={chosenItems().length === 0}
            onClick={() => setSigning(true)}
          >
            Complete with signature
          </Button>
        </footer>
      </div>

      <SignaturePadModal
        open={signing}
        title={`${person.full_name} · sign for ${station} returns`}
        onClose={() => setSigning(false)}
        onConfirm={onSignatureConfirm}
      />
    </>
  );
}
